import {AxisScaleOutput} from '@visx/axis';
import {PickD3Scale} from '@visx/scale';
import {ScaleTime, timeFormat} from 'd3';
import moment from 'moment';

import {VisxScaleType} from 'ui-library/charting/internals/VisxUtilityTypes';
import prettyChartNumber from 'ui-library/charting/util/prettyChartNumber';
import {assertNever} from 'util/Types';

import truncateToMaxLengthLabel from './truncateLabels';

export type TickLabelFormatter = (tickLabel: unknown) => string;

export default function getTickLabelFormatter<TScaleType extends VisxScaleType>(
  scaleType: TScaleType,
  scale: PickD3Scale<TScaleType, AxisScaleOutput>,
  suggestedNumTicks: number
): TickLabelFormatter {
  switch (scaleType) {
    case VisxScaleType.LINEAR:
    case VisxScaleType.LOG:
      return (value: number) => prettyChartNumber(value);
    case VisxScaleType.BAND:
    case VisxScaleType.POINT:
      return (value: string) => truncateToMaxLengthLabel(value);
    case VisxScaleType.UTC:
      return getDatetimeTickLabelFormatter(
        scale as PickD3Scale<VisxScaleType.UTC, AxisScaleOutput>,
        suggestedNumTicks
      );
    default:
      throw new Error(`Unexpected scale type ${assertNever(scaleType)}`);
  }
}

const UNITS: Array<keyof typeof UNIT_TO_FORMAT> = [
  'years',
  'months',
  'days',
  'hours',
  'minutes',
  'seconds',
  'milliseconds',
];
const UNIT_TO_FORMAT = {
  // Example input timestamp: 2021-01-18T20:16:25.140
  years: timeFormat('%Y'), // Output: 2021
  months: timeFormat('%b %Y'), // Output: Jan 2021
  days: timeFormat('%b %-d'), // Output: Jan 18
  hours: timeFormat('%-I:%M %p'), // Output: 8:16 PM
  minutes: timeFormat('%-I:%M %p'), // Output: 8:16 PM
  seconds: timeFormat('%-I:%M:%S %p'), // Output: 8:16:25 PM
  milliseconds: timeFormat('%-I:%M:%S.%L %p'), // Output: 8:16:25.140 PM
};
const HOURS_ACROSS_MULTIPLE_DAYS_FORMAT = timeFormat('%b %-d %-I:%M %p'); // Output: Jan 1 8:16 PM

/**
 * Return a formatter for tick labels of the given scale.
 *
 * For each time unit from largest to smallest, computes the number of those units in the scale's domain, and
 * if greater than numTicks, uses the formatting for that unit.
 *
 * For example, for the domain from Jan 1 2021 - March 3 2021, with 8 ticks:
 *  - Interval has 0 years
 *  - Interval has 2 months
 *  - Interval has 62 days, which is >8, so use day formatting
 *
 * This ensures we have informative labels for the time scale, without repeating labels.
 */
export function getDatetimeTickLabelFormatter(
  scale: ScaleTime<AxisScaleOutput, AxisScaleOutput>,
  suggestedNumTicks: number
): (d: Date) => string {
  const [earliest, latest] = scale.domain().map((datetime) => moment.utc(datetime));
  const numTicks = scale.ticks(suggestedNumTicks).length;
  const unit = UNITS.find((unit) => latest.diff(earliest, unit) >= numTicks) ?? 'milliseconds';

  return (d: Date) => {
    // Day formatting is confusing when interval spans day boundaries, and including the date makes it clearer
    if (unit === 'hours' && latest.diff(earliest, unit) >= 24) {
      // eslint-disable-next-line new-cap
      return HOURS_ACROSS_MULTIPLE_DAYS_FORMAT(d);
    }
    return UNIT_TO_FORMAT[unit](d);
  };
}
